import { Observable } from 'rxjs';
import { HydrographArgs } from './hydrograph';
import { HGGraphData } from './hydrographNEW';
import { EntityData } from './scatter-data';
import { SortDirection } from '@angular/material/sort';
import { LocationDashboardFilterData } from './location-dashboard-filter-data';
import { LocationData } from './locations-entities-data';
import { SelectableGroup } from './selectable';
import { DataEditPreview, StoredEdit, UpdatePointForSG, UpdatePoints } from './data-edit';
import { ConfirmationEntitiesEnum, SavedConfiguration } from './view-data-filter';


export enum SeparateWindowActionTypes {
    ping = 'Ping',
    pong = 'Pong',
    routeChange = 'RouteChange',
    locationChange = 'LocationChange',
    dataHover = 'dataHover',
    enterEdit = 'enterEdit',
    cancelEdit = 'cancelEdit',
    dateClose = 'dateClose',
    hgEntitiesChange = 'hgEntitiesChange',
    sgEntitiesChange = 'sgEntitiesChange',
    dataAverageChange = 'dataAverageChange',
    annotationChange = 'annotationChange',
    selectSnapPoints = 'selectSnapPoints',
    ignoreSGpoints = 'ignoreSGpoints',
    successSnap = 'successSnap',
    applyEditsToSg = 'applyEditsToSg',
    hgPreviewResponse = 'hgPreviewResponse',
    zoomPoints = 'zoomPoints',
    submitEdits = 'submitEdits',
    close = 'close',
    revertChanges = 'revertChanges',
    siltChange = 'siltChange',
    blockEditor = 'blockEditor',
    loadConfig = 'loadConfig',
    updateConfigs = 'updateConfigs',
    updateStoredEdits = 'updateStoredEdits',
    undoEdit = 'undoEdit',
    redoEdit = 'redoEdit',
    confirmationEntity = 'confirmationEntity',
    tracerPosition = 'tracerPosition',
}

export type Action = ActionTypePayload & { destinationId?: string; };

export type ActionTypePayload =
    { type: SeparateWindowActionTypes.ping; payload: string; }
    | { type: SeparateWindowActionTypes.pong; payload: LocationDashboardFilterData; }
    | { type: SeparateWindowActionTypes.routeChange; payload: string; }
    | { type: SeparateWindowActionTypes.locationChange; payload: LocationData; }
    | { type: SeparateWindowActionTypes.dataHover; payload: { id: 0 | 1; dateTime: number; }; }
    | { type: SeparateWindowActionTypes.locationChange; payload: LocationData; }
    | { type: SeparateWindowActionTypes.enterEdit; payload: string; }
    | { type: SeparateWindowActionTypes.cancelEdit; payload: void; }
    | { type: SeparateWindowActionTypes.dateClose; payload: { start: Date; end: Date; }; }
    | { type: SeparateWindowActionTypes.hgEntitiesChange; payload: SelectableGroup[]; }
    | { type: SeparateWindowActionTypes.sgEntitiesChange; payload: SelectableGroup[]; }
    | { type: SeparateWindowActionTypes.dataAverageChange; payload: number; }
    | { type: SeparateWindowActionTypes.annotationChange; payload: AnnotationSettings; }
    | { type: SeparateWindowActionTypes.selectSnapPoints; payload: { selected: number[]; snapped: number[]; }; }
    | { type: SeparateWindowActionTypes.ignoreSGpoints; payload: { res: DataEditPreview; points: EntityData[]; }; }
    | { type: SeparateWindowActionTypes.successSnap; payload: number; }
    | { type: SeparateWindowActionTypes.applyEditsToSg; payload: UpdatePointForSG[]; }
    | { type: SeparateWindowActionTypes.hgPreviewResponse; payload: { res: DataEditPreview; points: UpdatePoints[]; }; }
    | { type: SeparateWindowActionTypes.zoomPoints; payload: number[]; }
    | { type: SeparateWindowActionTypes.submitEdits; payload: void; }
    | { type: SeparateWindowActionTypes.close; payload: void; }
    | { type: SeparateWindowActionTypes.revertChanges; payload: void; }
    | { type: SeparateWindowActionTypes.siltChange; payload: boolean; }
    | { type: SeparateWindowActionTypes.blockEditor; payload: { hg: DataEditPreview; sg: UpdatePointForSG[]; }; }
    | { type: SeparateWindowActionTypes.loadConfig; payload: SavedConfiguration; }
    | { type: SeparateWindowActionTypes.updateConfigs; payload: void; }
    | { type: SeparateWindowActionTypes.updateStoredEdits; payload: { edits: StoredEdit[]; currentTS: string; }; }
    | { type: SeparateWindowActionTypes.undoEdit; payload: number; }
    | { type: SeparateWindowActionTypes.redoEdit; payload: number; }
    | { type: SeparateWindowActionTypes.confirmationEntity; payload: ConfirmationEntitiesEnum; }
    | { type: SeparateWindowActionTypes.tracerPosition; payload: boolean; }
;

export interface ViewData {
    title: string;
    subTitle: string;
    start: Date;
    end: Date;
    displayGroups: DisplayGroup[];
}

export interface EntityResponseItem {
    entityId?: number;
    entityName?: string;
    entityDisplayName?: string;
    entityShortDisplayName?: string;
    storageUnitCategory?: number;
    displayGroup?: number;
    displayGroupPrecendece?: number;
    isPreDefined?: boolean;
    displayGroupName?: string;
    flowDataType?: number;
    totallingMethod?: number;
    summaryMethod?: number;
    isRain?: boolean;
    rate?: number;
    readings?: [];
}

export interface DisplayGroup {
    id: number;
    axis: Axis;
    entityInformation: EntityInformation[];
    entityData: Array<ViewDataEntityData>;
    unit: string;
    precision?: number;
    annotationInformation: AnnotationInformation[];
    annotations: Array<ViewDataEntityData>;
    style?: string;
    sortOrder?: number;
}

export interface ViewDataEntityData {
    ts: Date;
    E: { [entityId: number]: number };
    Q: number;
}

export interface EntityInformation {
    id: number;
    name: string;
    color: number;
    storageUnitCategory?: number;
    colorHex?: string;
}

export interface EntityInformationData {
    id?: number;
    entityName: string;
    entityData: Array<DepthPoint>;
    color: string;
    unitOfMeasure: string;
    precision?: number;
    axis?: Axis;
    storageUnitCategory?: number;
    style?: string;
}

export interface EntitySeries {
    legendIndex?: number;
    visible?: boolean;
    name: string;
    id?: number;
    displayGroupID?: number;
    type: string;
    data?: Array<DepthPoint>;
    showInLegend: boolean;
    color: string;
    dashStyle?: string;
    dataGrouping: { enabled: false };
    zIndex: number;
    lineWidth: number;
    unitOfMeasure: string;
    precision: number;
    axis?: Axis;
    yAxis: number;
    marker?: Marker;
    step: boolean;
    style?: string;
    turboThreshold?: number;
    connectNulls?: boolean;
    plotBands?: Array<{ from: number; to: number; color?: string }>;
    correctedData?: EntitySeries;
    locationName?: string;
    startDate?: string;
    isMultipleOverlayEnabled?: boolean;
    editedData?: EntitySeries;
    pointStart?: number;
    plotOptions?: { series?: { animation?: boolean; marker?: { enabled?: boolean } } };
}

export interface AnnotationInformation {
    id: number;
    name: string;
    color: number;
    storageUnitCategory: number;
    colorHex?: string;
}

/**
 * Represents the scaling details for hydrograph
 */
interface Axis {
    label?: string;
    minimum: number;
    maximum: number;
}

/**
 * Represents the marker detials for Hydrograph
 */
interface Marker {
    symbol?: string;
    radius?: number;
    enabled?: boolean;
    color?: string;
}

/**
 * Represents the Annotation Series for Hydrograph
 */

export interface AnnotationSeries {
    name: string;
    type: string;
    data: Array<DepthPoint>;
    showInLegend: boolean;
    color: string;
    dashStyle?: string;
    dataGrouping?: { enabled: false };
    unitOfMeasure: string;
    precision: number;
    marker?: Marker;
    lineWidth: number;
    location: string;
    zIndex?: number;
    step?: string;
}

/**
 * Represents the Entity Details for Hydrograph
 */
export interface EntityDetails {
    label: string;
    minimum?: number;
    maximum?: number;
    unit?: string;
    storageCategory?: number;
}

/**
 * Represents the y-axis for Hydrograph
 */
export interface YAxis {
    allowDecimals: boolean;
    title: { text?: string; enabled?: boolean; style?: { color?: string; fontSize?: string } };
    top: string;
    height: string;
    min: number;
    max: number;
    offset: number;
    opposite?: boolean;
    reversed?: boolean;
    maxPadding?: number;
    startOnTick?: boolean;
    endOnTick?: boolean;
    tickInterval?: number;
    showFirstLabel?: boolean;
    showLastLabel?: boolean;
    visible?: boolean;
    tickAmount: number;
    tickPositions?: Array<number>;
    gridLineWidth?: number;
}

export interface DepthPoint {
    x: number;
    y: number;
    segmentColor?: string;
    color?: string;
}

export interface AnnotationSettings {
    isCurveEnabled: boolean;
    isToolbarEnabled?: boolean;
    isPipeHeight: boolean;
    isManholeDepth: boolean;
    isSilt: boolean;
    isDataQuality: boolean;
    isRainOnTop: boolean;
    isShowEdits: boolean;
    isScatterInvert?: boolean;
    isHighLevel: boolean;
    isHighHigh: boolean;
    isHighFlow: boolean;
    isLowDepth: boolean;
    isIsoQ?: boolean;
    isPipeOverlay?: boolean;
    isBestFit?: boolean;
    isToleranceLines?: boolean;
    isSSCurve?: boolean;
    isCWRcurve?: boolean;
    isManningDesign: boolean;
    isLanfearColl: boolean;
    isManualLinear?: boolean;
    isManualSmooth?: boolean;
    isConfirmationPoints?: boolean;
    isFroude?: boolean;
    includeZeroRainValues?: boolean;
}

export interface DateTimeSetting {
    tooltipTimeFormat: string;
    xAxisDateFormat: string;
}

export interface DataEditingData {
    dateTime?: Date;
    dateTimeUTC?: Date;
    entity?: string;
    rawData?: number;
    suggestedData?: string;
    modifiedValue?: string;
    originalModifiedValue?: string;
    id?: number;
    displayGroupId?: number;
    displayUnit?: string;
    isSelected?: boolean;
    dataEditingArray?: Array<DataEditingData>;
    whichPoint?: DataEditingData;
    whichActionToPerform?: boolean;
    enablePreview?: boolean;
    timeStamp?: Date;
    eid?: string;
    reading?: number;
    reason?: string;
    isFlagged?: boolean;
}

export interface EditedData {
    dateTime?: Date;
    entity?: string;
    rawData?: number;
    suggestedData?: string;
    originalModifiedValue?: string;
    id?: number;
    displayGroupId?: number;
    displayUnit?: string;
    isSelected?: boolean;
    dataEditingArray?: Array<DataEditingData>;
    whichPoint?: DataEditingData;
    whichActionToPerform?: boolean;
    pointExists?: boolean;
    isFlagged?: boolean;
}

export interface FlaggedPoint {
    entityId: number;
    timestamp: number;
    reading: number;
}

export interface DataApprovalRange {
    startDate: Date;
    endDate: Date;
}

export interface ApproveDataDialogData {
    endDate: Date;
    startDate: Date | null;
    locationName: string;
    prevApprStartDate: Date | null;
    prevApprEndDate: Date | null;
    approvedBy: string;
}

export interface HydroPoint {
    dateTime: number;
    reading: number;
    timeStamp: string;
    ignore: boolean;
}

export interface PointsGroup {
    id: number;
    startDate: Date;
    endDate: Date;
    points: HydroPoint[];
}

export interface DataEditingRow {
    timeStampSelected: number;
    rawDataSelected: number;
    entity?: string;
    selectedTimeStamps?: DataEditingSelectedTimeStamps;
}

export interface DataEditingSelectedTimeStamps {
    minValue: number;
    maxValue: number;
}

export interface LaunchDataEditingTable {
    hydrographParams: HydrographArgs;
    customerId: number;
    locationId: number;
    isDisplayAutoCorrectedData: boolean;
}

export interface ScatterSeriesTooltip {
    useHTML: boolean;
    headerFormat: string;
    shared?: boolean;
    pointFormatter?: () => string;
    positioner?: (labelWidth: number, labelHeight: number, point) => ({ x: number; y: number });
    stickOnContact?: boolean;
}

export interface MatSortData {
    active: string;
    direction: SortDirection;
  }

export interface BestFitCurve {
    name: string;
    data: EntityData[];
    type: string;
    dashStyle?: string;
    color: string;
    width?: number;
    findNearestPointBy?: string;
    zIndex?: number;
    tooltip?: ScatterSeriesTooltip;
    label?: { enabled?: boolean };
    smoothCurve?: boolean;
}

export interface EntityInfo {
    id: number;
    name: string;
    color: number;
    displayGroupID: number;
    precision?: number;
    unit?: string;
    axisLabel: string;
    storageUnitCategory?: number;
    colorHex?: string;
}

export interface compactAnnotations {
    [key: number]: string;
}

export interface compactData {
    [key: number]: string;
}

export interface OptimizedViewData {
    annotationIds: Array<number>;
    compactAnnots: compactAnnotations;
    compactData: compactData;
    displayGroups: OptimizedDisplayGroup[];
    end: Date;
    entityIds: Array<number>;
    start: Date;
    subTitle: string;
    title: string;
    enablePreview?: boolean;
}

export interface optimizedAnnotations {
    color: string;
    id: number;
    name: string;
    unit: number;
    factor: number;
    offset: number;
}

export interface optimizedEntities {
    color: string;
    id: number;
    name: string;
    unit: number;
}

export interface ChartPointMap {
    [key: string]: HGGraphData[]
}

export interface OptimizedDisplayGroup {
    annotations: Array<optimizedAnnotations>;
    entities: Array<optimizedEntities>;
    id: number;
    label: string;
    precision?: number;
    sort?: number;
    style?: string;
    unit?: string;
}

export interface SSCurve {
    name?: string;
    data?: EntityData[];
    type: string;
    dashStyle?: string;
    color?: string;
    width?: number;
    zIndex?: number;
    tooltip?: ScatterSeriesTooltip;
}

export interface DataEditingReasons {
    id: number;
    reason: string;
}

export interface SyncObject {
    editedTimestamp: number;
    editedValue: number;
    editedEntity: number;
}

export interface DateRangeValues {
    startDate: Date;
    endDate: Date;
}

export enum DISPLAY_GROUP_LABELS {
    Depth = 'Depth',
    Velocity = 'Velocity',
    Flow = 'Flow',
    Qcontinuity = 'QContinuity',
    Rain = 'Rain',
    Temp = 'Temperature',
}

export enum DataEditingActionType {
    CopyPaste,
    DragDrop,
    Flagged,
    MultiPoint,
    None,
    UnFlagged,
    Zoom,
}

export enum AdsChartType {
    HYDRO_GRAPH = 0,
    SCATTER_GRAPH,
}

export enum QUICK_DATE_RANGES {
    TODAY = 'today',
    LAST_WEEK = 'lastWeek',
    LAST_MONTH = 'lastMonth',
    PREVIOUS_MONTH = 'previousMonth',
    LAST_THREE_MONTHS = 'lastThreeMonths',
    LAST_SIX_MONTHS = 'lastSixMonths',
    LAST_YEAR = 'lastYear',
}

export enum MultiEditArithmeticType {
    Add = 0,
    Substract,
    Multiply,
    Divide,
    Equal,
}

export enum HydrographTickInterval {
    daily = 1,
    Weekly = 7,
    Monthly = 30,
    ThreeMonths = 90,
    SixMonths = 180,
    Year = 365,
}

export enum GenerationOptions {
    CURRENT,
    LAST_30,
    LAST_60,
    LAST_90,
}

export interface ScatterRevertData {
    x: number;
    y: number;
    ts: number;
    dataQuality?: string;
    flagged?: number;
    rest?: string[];
}

export interface SeriesProperty {
    lid: number;
    eid?: number;
    color: string;
    eidX?: number;
    eidXName?: string;
    eidY?: number;
    eidYName?: string;
    locationName?: string;
    startDate?: Date;
    endDate?: Date;
    request?: Observable<OptimizedViewData>;
    data?: OptimizedViewData;
    entityName?: string;
    originLocationId?: number;
    id?: number;
}

export interface ConfirmationData {
    type?: string;
    dateRange?: {
        start: string;
        end: string
    }
}

export interface ScatterHighlightRange {
    minValue: number;
    maxValue: number;
}

export interface ScatterSelectionBounds {
    xMinValue: number;
    xMaxValue: number;
    yMinValue: number;
    yMaxValue: number;
}

export interface HydrographSelectionBounds {
    xMinValue: number;
    xMaxValue: number;
    yMinValue?: number;
    yMaxValue?: number;
}

export interface ServiceFlaggedPointData {
    dateTime: number;
    reading: number;
    timeStamp: string;
    ignore: boolean;
}

export interface EntityFlaggedPointData {
    id: number;
    startDate: Date;
    endDate: Date;
    points: ServiceFlaggedPointData[];
}

export interface CopiedPointData {
    id: number;
    dateTime: number;
    dateTimeUtc: string;
    y: number;
    x: Date;
}

export interface MultiPointEditData {
    id: number;
    dateTime: number;
    x: Date;
    y: number;
    dateTimeUTC: string;
}

export interface HighlightPoint {
    x: number;
    y: number;
    dateTime: number;
    series: { name: string };
}

export interface ScatterPointElement {
    x?: number;
    y?: number;
    dateTime?: number | Date;
    color?: string;
    update?: (options: object, redraw?: boolean) => void;
    select?: (selected?: boolean) => void;
}

export interface DataEditingApiResponse {
    pageData: Array<{
        dt: Date;
        ts: Date;
        id: number;
        r: number;
    }>;
    entities: { [key: number]: string };
    count: number;
}
